import { z } from 'zod';
import { ASPECTOS_CERVIX, CAUSAS_NO_ADECUADA, CRITERIOS_IFCPC, DIAGNOSTICOS_COLPOSCOPICOS, ETIQUETAS_DE_CALIDAD, FASES_DE_CAPTURA, HALLAZGOS_MISCELANEOS, MAX_MEDICIONES_POR_IMAGEN, MAX_VERTICES_POLIGONO, MAX_IMAGENES_COLPOSCOPIA, MAX_TEXTO_LIBRE, MIN_HALLAZGOS, PRESENCIA, RESULTADOS_SCHILLER, RESULTADO_ACCESORIO, UNION_ESCAMOCOLUMNAR, VALORACION_COLPOSCOPIA, ZONAS_TRANSFORMACION, opcionesDelEje, } from '../catalogos/colposcopia.js';
import { problemaDelPoligono } from '../colposcopio/geometria.js';
const textoLibre = (maximo = MAX_TEXTO_LIBRE) => z.string().trim().max(maximo).default('');
/** Opcional de catalogo: se admite la cadena vacia como "sin dato". */
const deCatalogo = (valores) => z.union([z.literal(''), z.enum(valores)]).default('');
/**
 * Cada eje se califica eligiendo hallazgos de sus tres grados. Se valida contra
 * el catalogo del eje, asi que no entra un hallazgo de otro eje por error.
 */
function esquemaEje(eje) {
    const permitidos = opcionesDelEje(eje);
    return z
        .array(z.string())
        .default([])
        .refine((valores) => valores.every((valor) => permitidos.includes(valor)), {
        message: `Hallazgo no valido para el eje ${eje}`,
    });
}
export const esquemaEvaluación = z.object({
    valoracion: deCatalogo(VALORACION_COLPOSCOPIA),
    causaNoAdecuada: deCatalogo(CAUSAS_NO_ADECUADA),
    cervix: deCatalogo(ASPECTOS_CERVIX),
    unionEscamocolumnar: deCatalogo(UNION_ESCAMOCOLUMNAR),
    zonaTransformacion: deCatalogo(ZONAS_TRANSFORMACION),
    schiller: deCatalogo(RESULTADOS_SCHILLER),
    epitelioAcetoblanco: deCatalogo(PRESENCIA),
});
export const esquemaCriterios = z.object({
    epitelio: esquemaEje('epitelio'),
    bordeSuperficie: esquemaEje('bordeSuperficie'),
    angioarquitectura: esquemaEje('angioarquitectura'),
    captacionYodo: esquemaEje('captacionYodo'),
});
export const esquemaAccesorios = z.object({
    vaginoscopia: deCatalogo(RESULTADO_ACCESORIO),
    vulvoscopia: deCatalogo(RESULTADO_ACCESORIO),
    anoscopia: deCatalogo(RESULTADO_ACCESORIO),
});
/** Coordenada normalizada sobre la imagen: 0 es el borde izquierdo/superior, 1 el opuesto. */
const coordenada = z.number().finite().min(0).max(1);
const esquemaPunto = z.object({ x: coordenada, y: coordenada }).strict();
/**
 * Una medicion sobre la imagen (ADR-017). El cliente manda el tipo, los puntos
 * y la nota; los valores (px, mm, modo, hora, cuadrante) los RECALCULA el
 * servidor al firmar. Si llegan, se ignoran.
 */
export const esquemaMedicion = z
    .object({
    tipo: z.enum(['distancia', 'area']),
    puntos: z.array(esquemaPunto).min(2).max(MAX_VERTICES_POLIGONO),
    nota: z.string().trim().max(200).default(''),
    valorPx: z.number().optional(),
    valorMm: z.number().nullable().optional(),
    modo: z.enum(['calibrado', 'nominal']).optional(),
    posicionHoraria: z.number().int().min(1).max(12).nullable().optional(),
    cuadrante: z.enum(['Q1', 'Q2', 'Q3', 'Q4']).nullable().optional(),
})
    .superRefine((m, ctx) => {
    if (m.tipo === 'distancia' && m.puntos.length !== 2) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['puntos'],
            message: 'Una distancia lleva exactamente dos puntos',
        });
    }
    if (m.tipo === 'area') {
        const problema = problemaDelPoligono(m.puntos);
        if (problema) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['puntos'], message: problema });
        }
    }
});
/**
 * Imagen del estudio. `archivoId`, `etiqueta` y `orden` son los de siempre; lo
 * demas llega con la captura del colposcopio (ADR-017) y es OPCIONAL y sin
 * valor por defecto: un estudio firmado antes no cambia de forma (ni de hash).
 *
 * `sha256`, `ancho`, `alto` y `escala.pxPorMm` los pone el servidor a partir
 * del archivo y de la calibracion; si el cliente los manda, se ignoran.
 */
export const esquemaImagenColposcopia = z.object({
    archivoId: z.string().regex(/^[0-9a-fA-F]{24}$/, 'Identificador de imagen inválido'),
    etiqueta: z.string().trim().max(120).default(''),
    orden: z.coerce.number().int().min(0).default(0),
    origen: z.enum(['captura', 'subida']).optional(),
    fase: z.enum(FASES_DE_CAPTURA).optional(),
    calidad: z
        .object({
        puntuacion: z.number().min(0).max(100),
        etiqueta: z.enum(ETIQUETAS_DE_CALIDAD),
    })
        .strict()
        .optional(),
    cervix: z
        .object({ cx: coordenada, cy: coordenada, r: z.number().finite().positive().max(1) })
        .strict()
        .optional(),
    /** Calibracion del dispositivo con que se tomo. Sin ella, la escala es nominal. */
    calibracionId: z
        .string()
        .regex(/^[0-9a-fA-F]{24}$/)
        .nullable()
        .optional(),
    mediciones: z.array(esquemaMedicion).max(MAX_MEDICIONES_POR_IMAGEN).optional(),
});
/** Calibracion de un dispositivo de captura a una resolucion (ADR-017). */
export const esquemaCrearCalibracion = z
    .object({
    /** Huella SHA-256 del identificador del dispositivo en el navegador. */
    huellaDispositivo: z.string().regex(/^[0-9a-f]{64}$/, 'Huella inválida'),
    etiquetaDispositivo: z.string().trim().max(120).default(''),
    ancho: z.number().int().positive().max(10_000),
    alto: z.number().int().positive().max(10_000),
    referenciaPx: z.number().finite().positive(),
    referenciaMm: z.number().finite().positive().max(1000),
})
    .strict();
export const esquemaBuscarCalibracion = z.object({
    huella: z.string().regex(/^[0-9a-f]{64}$/),
    ancho: z.coerce.number().int().positive(),
    alto: z.coerce.number().int().positive(),
});
export const esquemaCrearColposcopia = z
    .object({
    pacienteId: z.string().regex(/^[0-9a-fA-F]{24}$/, 'Paciente inválido'),
    /** Fecha del estudio, en formato AAAA-MM-DD. */
    fecha: z
        .string()
        .trim()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use el formato AAAA-MM-DD'),
    motivo: textoLibre(300),
    evaluacion: esquemaEvaluación.default({}),
    criterios: esquemaCriterios.default({}),
    accesorios: esquemaAccesorios.default({}),
    miscelaneos: z.array(z.enum(HALLAZGOS_MISCELANEOS)).default([]),
    hallazgos: z
        .string()
        .trim()
        .min(MIN_HALLAZGOS, 'Describa los hallazgos del estudio')
        .max(MAX_TEXTO_LIBRE),
    diagnostico: z.enum(DIAGNOSTICOS_COLPOSCOPICOS),
    observaciones: textoLibre(),
    plan: textoLibre(),
    tomaBiopsia: z.boolean().default(false),
    sitiosBiopsia: textoLibre(300),
    imagenes: z.array(esquemaImagenColposcopia).max(MAX_IMAGENES_COLPOSCOPIA).default([]),
})
    .superRefine((valores, ctx) => {
    // Si el estudio no fue adecuado, hay que decir por que: es lo que justifica
    // repetirlo y lo primero que se busca al revisar el expediente.
    if (valores.evaluacion.valoracion === 'No adecuada' && !valores.evaluacion.causaNoAdecuada) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['evaluacion', 'causaNoAdecuada'],
            message: 'Indique la causa por la que el estudio no fue adecuado',
        });
    }
    if (valores.tomaBiopsia && !valores.sitiosBiopsia) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['sitiosBiopsia'],
            message: 'Indique de dónde se tomó la biopsia',
        });
    }
});
/** La edicion no permite mover el estudio a otro paciente. */
export const esquemaActualizarColposcopia = z.object({
    fecha: z
        .string()
        .trim()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    motivo: z.string().trim().max(300).optional(),
    evaluacion: esquemaEvaluación.partial().optional(),
    criterios: esquemaCriterios.partial().optional(),
    accesorios: esquemaAccesorios.partial().optional(),
    miscelaneos: z.array(z.enum(HALLAZGOS_MISCELANEOS)).optional(),
    hallazgos: z.string().trim().min(MIN_HALLAZGOS).max(MAX_TEXTO_LIBRE).optional(),
    diagnostico: z.enum(DIAGNOSTICOS_COLPOSCOPICOS).optional(),
    observaciones: z.string().trim().max(MAX_TEXTO_LIBRE).optional(),
    plan: z.string().trim().max(MAX_TEXTO_LIBRE).optional(),
    tomaBiopsia: z.boolean().optional(),
    sitiosBiopsia: z.string().trim().max(300).optional(),
    imagenes: z.array(esquemaImagenColposcopia).max(MAX_IMAGENES_COLPOSCOPIA).optional(),
    /** Por que se modifica un documento ya firmado. Queda en el rastro. */
    motivoEdicion: z.string().trim().min(5, 'Explique el motivo de la corrección').max(300),
});
export const esquemaListarColposcopías = z.object({
    pacienteId: z
        .string()
        .regex(/^[0-9a-fA-F]{24}$/)
        .optional(),
    pagina: z.coerce.number().int().min(1).default(1),
    porPagina: z.coerce.number().int().min(1).max(100).default(20),
});
/**
 * Grado mas alto alcanzado en el estudio.
 *
 * Resume los cuatro ejes en una sola señal, que es lo que permite ordenar una
 * lista de estudios por severidad sin abrir cada uno.
 */
export function gradoMaximo(criterios) {
    const ejes = ['epitelio', 'bordeSuperficie', 'angioarquitectura', 'captacionYodo'];
    let resultado = 'ninguno';
    for (const eje of ejes) {
        for (const hallazgo of criterios[eje]) {
            if (CRITERIOS_IFCPC[eje].mayores.includes(hallazgo))
                return 'mayores';
            if (CRITERIOS_IFCPC[eje].intermedios.includes(hallazgo))
                resultado = 'intermedios';
            else if (CRITERIOS_IFCPC[eje].menores.includes(hallazgo) &&
                resultado === 'ninguno') {
                resultado = 'menores';
            }
        }
    }
    return resultado;
}
//# sourceMappingURL=colposcopia.js.map